import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormColumns } from "components/form/FormColumns/FormColumns";
import { FormField } from "components/form/FormField/FormField";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea";
import { useSendSmsForPatient } from "features/comunications/hooks/useSendSmsForPatient";
import { validatePatientForSms } from "features/comunications/utils/validatePatientForSms";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { useSmsTemplates } from "../../hooks/useSmsTemplates";
import styles from "./SendSmsModal.module.css";

const formSchema = z.object({
  message: z.string().min(1, "Message is required"),
  smsTemplateId: z.string().optional(),
});

export const SendSmsModal = ({
  isOpen,
  onClose,
  patient,
  initialMessage = "",
}) => {
  const [patientErrors, setPatientErrors] = useState([]);

  const { data: smsTemplates = [], isLoading: isLoadingTemplates } =
    useSmsTemplates(patient?.id);
  const sendSmsMutation = useSendSmsForPatient();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      message: initialMessage,
      smsTemplateId: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      setPatientErrors(validatePatientForSms(patient));
      form.reset({ message: initialMessage, smsTemplateId: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient]);

  const handleClose = () => {
    setPatientErrors([]);
    form.reset();
    onClose();
  };

  const onSubmit = (values) => {
    if (patientErrors.length > 0) return;

    sendSmsMutation.mutate(
      { patientId: patient.id, message: values.message },
      { onSuccess: () => handleClose() }
    );
  };

  const handleSubmit = form.handleSubmit(onSubmit, onInvalidSubmit("SendSmsModal"));
  const hasPatientErrors = patientErrors.length > 0;
  const patientName = patient
    ? `${patient.firstName || ""} ${patient.surname || ""}`.trim()
    : "";

  return (
    <Dialog open={isOpen} title="Send SMS" onClose={handleClose} width={580}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {hasPatientErrors && (
            <div className={styles.errorBanner}>
              {patientErrors.map((err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </div>
          )}

          <FormColumns columns={2} gap={24}>
            <FormField name="recipientName" label="Recipient Name">
              <CustomInput value={patientName} disabled />
            </FormField>
            <FormField name="mobileNumber" label="Mobile Number">
              <CustomInput
                value={patient?.mobilePhone || "No phone number"}
                disabled
              />
            </FormField>
          </FormColumns>

          <FormField name="smsTemplateId" label="SMS Template (Optional)">
            <Controller
              name="smsTemplateId"
              control={form.control}
              render={({ field }) => (
                <Select
                  options={smsTemplates.map((t) => ({
                    value: String(t.id),
                    label: t.name,
                  }))}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);

                    if (value) {
                      const template = smsTemplates.find(
                        (t) => t.id === parseInt(value)
                      );

                      if (template) {
                        const currentMessage = form.getValues("message") || "";
                        form.setValue(
                          "message",
                          currentMessage
                            ? `${currentMessage}\n${template.template || ""}`
                            : template.template || ""
                        );
                      }
                    }
                  }}
                  placeholder={
                    isLoadingTemplates
                      ? "Loading templates..."
                      : "Select a template"
                  }
                  disabled={isLoadingTemplates}
                  fullWidth
                />
              )}
            />
          </FormField>

          <FormField name="message" label="Message">
            <Textarea
              {...form.register("message")}
              placeholder="Write your message here..."
              rows={6}
            />
          </FormField>
        </form>
      </FormProvider>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={handleClose}
          disabled={sendSmsMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={sendSmsMutation.isPending || hasPatientErrors}
        >
          {sendSmsMutation.isPending ? "Sending..." : "Send SMS"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
